import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  MessageSquare, 
  Send, 
  X, 
  Loader2, 
  Bot, 
  User, 
  ChevronDown,
  FileText,
  Calculator,
  Scale,
  Sparkles,
  ExternalLink,
  Copy,
  Check
} from 'lucide-react';
import { intelligenceApi, ChatMessage, ChatResponse, Citation, ToolCall } from '../services/intelligenceApi';
import { toast } from 'sonner';

interface CaseIntelligenceChatProps {
  reportId: string;
  reportName?: string;
  isOpen: boolean;
  onClose: () => void;
}

// Tool icons mapping
const TOOL_ICONS: Record<string, React.ElementType> = {
  'get_case_summary': FileText,
  'get_settlement_range': Scale,
  'run_sensitivity_analysis': Calculator,
  'run_monte_carlo': Calculator,
  'run_whatif_scenario': Calculator,
  'get_attorney_perspective': Scale,
  'get_adjuster_perspective': Scale,
  'get_forensic_perspective': FileText,
  'search_case_documents': FileText,
  'get_issue_status': FileText,
};

// Tool display names
const TOOL_NAMES: Record<string, string> = {
  'get_case_summary': 'Case Summary',
  'get_settlement_range': 'Settlement Range',
  'run_sensitivity_analysis': 'Sensitivity Analysis',
  'run_monte_carlo': 'Monte Carlo Simulation',
  'run_whatif_scenario': 'What-If Scenario',
  'get_attorney_perspective': 'Attorney Perspective',
  'get_adjuster_perspective': 'Adjuster Perspective',
  'get_forensic_perspective': 'Forensic Analysis',
  'search_case_documents': 'Document Search',
  'get_issue_status': 'Issue Status',
  'get_transaction_details': 'Transaction Details',
  'run_game_theory_analysis': 'Game Theory Analysis',
  'run_litigation_economics': 'Litigation Economics',
};

export default function CaseIntelligenceChat({ 
  reportId, 
  reportName,
  isOpen, 
  onClose 
}: CaseIntelligenceChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [suggestedFollowups, setSuggestedFollowups] = useState<string[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Send message handler
  const sendMessage = useCallback(async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setSuggestedFollowups([]);

    try {
      const response: ChatResponse = await intelligenceApi.sendMessage(
        reportId,
        text,
        conversationId || undefined
      );

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        tool_calls: response.tool_calls,
        citations: response.citations
      };

      setMessages(prev => [...prev, assistantMessage]);
      setConversationId(response.conversation_id);
      setSuggestedFollowups(response.suggested_followups || []);

    } catch (error: any) {
      toast.error(error.message || 'Failed to send message');
      // Remove the user message on error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, reportId, conversationId]);

  // Handle keyboard input
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Copy message to clipboard
  const copyMessage = (content: string, id: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Render citations
  const renderCitations = (citations: Citation[]) => {
    if (!citations || citations.length === 0) return null;
    
    return (
      <div className="mt-2 pt-2 border-t border-gray-100">
        <p className="text-xs text-gray-500 mb-1">Sources:</p>
        <div className="flex flex-wrap gap-1">
          {citations.map((citation, idx) => (
            <span 
              key={idx}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full"
            >
              <FileText className="w-3 h-3" />
              {citation.source_name}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Render tool calls
  const renderToolCalls = (toolCalls: ToolCall[]) => {
    if (!toolCalls || toolCalls.length === 0) return null;

    return (
      <div className="mt-2 flex flex-wrap gap-1">
        {toolCalls.map((tc, idx) => {
          const Icon = TOOL_ICONS[tc.tool_name] || Calculator;
          const name = TOOL_NAMES[tc.tool_name] || tc.tool_name;
          
          return (
            <span 
              key={idx}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-purple-50 text-purple-700 rounded-full"
              title={`Parameters: ${JSON.stringify(tc.parameters)}`}
            >
              <Icon className="w-3 h-3" />
              {name}
            </span>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed bottom-4 right-4 w-[420px] h-[600px] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col z-50"
      data-testid="intelligence-chat"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-xl">
        <div className="flex items-center gap-2 text-white">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Case Intelligence</h3>
            <p className="text-xs text-white/70 truncate max-w-[200px]">
              {reportName || reportId}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
          data-testid="close-chat"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
              <Bot className="w-8 h-8 text-purple-600" />
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Ask me anything about this case</h4>
            <p className="text-sm text-gray-500 mb-4">
              I can analyze data, run calculations, and provide different perspectives.
            </p>
            <div className="space-y-2 w-full">
              {[
                "What's the summary of this case?",
                "Show me the settlement range",
                "What would the adjuster challenge?"
              ].map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => sendMessage(suggestion)}
                  className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-gray-700"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                data-testid={`message-${message.role}`}
              >
                {/* Avatar */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  message.role === 'user' 
                    ? 'bg-blue-100' 
                    : 'bg-purple-100'
                }`}>
                  {message.role === 'user' 
                    ? <User className="w-4 h-4 text-blue-600" />
                    : <Bot className="w-4 h-4 text-purple-600" />
                  }
                </div>

                {/* Message content */}
                <div className={`flex-1 max-w-[85%] ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block px-4 py-2 rounded-xl ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {message.role === 'assistant' ? (
                      <div className="prose prose-sm max-w-none">
                        <div 
                          className="text-sm whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{ 
                            __html: message.content
                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                              .replace(/\n## (.*?)\n/g, '<h3 class="font-semibold text-base mt-3 mb-1">$1</h3>')
                              .replace(/\n- /g, '<br/>• ')
                          }}
                        />
                      </div>
                    ) : (
                      <p className="text-sm">{message.content}</p>
                    )}
                  </div>

                  {/* Tool calls and citations for assistant messages */}
                  {message.role === 'assistant' && (
                    <>
                      {renderToolCalls(message.tool_calls || [])}
                      {renderCitations(message.citations || [])}
                      
                      {/* Copy button */}
                      <button
                        onClick={() => copyMessage(message.content, message.id)}
                        className="mt-1 text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                      >
                        {copiedId === message.id ? (
                          <>
                            <Check className="w-3 h-3" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            Copy
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-purple-600" />
                </div>
                <div className="bg-gray-100 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Suggested follow-ups */}
      {suggestedFollowups.length > 0 && !isLoading && (
        <div className="px-4 py-2 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-2">Suggested follow-ups:</p>
          <div className="flex flex-wrap gap-1">
            {suggestedFollowups.map((followup, idx) => (
              <button
                key={idx}
                onClick={() => sendMessage(followup)}
                className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100 transition-colors"
                data-testid={`followup-${idx}`}
              >
                {followup}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="p-3 border-t border-gray-200">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this case..."
            className="flex-1 resize-none border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent max-h-24"
            rows={1}
            disabled={isLoading}
            data-testid="chat-input"
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || isLoading}
            className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="send-button"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Floating toggle button for opening chat
// Positioned above the existing AdjusterQABot button
export function IntelligenceChatButton({ 
  onClick, 
  hasUnread = false 
}: { 
  onClick: () => void;
  hasUnread?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-24 right-6 w-12 h-12 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-50"
      data-testid="open-intelligence-chat"
      title="Case Intelligence AI"
    >
      <Sparkles className="w-5 h-5" />
      {hasUnread && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white" />
      )}
    </button>
  );
}
